/**
 * iLing Strength Utilities
 * 
 * Maps vocabulary strength (0.0 - 1.0) to visual feedback.
 * Color scheme: New (red) → Learning (yellow) → Familiar (green) → Known (neutral/white)
 */

/**
 * Get CSS color for a strength value
 * Using iLing color scheme:
 * - 0.0-0.3: Unknown (coral red)
 * - 0.3-0.7: Learning (amber)
 * - 0.7-1.0: Known (neutral – inherits foreground text color)
 */
export const getWordColor = (strength) => {
  const s = Math.max(0, Math.min(1, strength || 0));
  
  if (s < 0.3) {
    return 'var(--strength-new)';      // Red – New
  } else if (s < 0.7) {
    return 'var(--strength-learning)'; // Yellow – Learning
  } else {
    return 'hsl(var(--foreground))';   // White/Black – Known (neutral)
  }
};

/**
 * Get status class name for styling
 */
export const getStatusClass = (strength) => {
  const s = Math.max(0, Math.min(1, strength || 0));
  
  if (s < 0.3) return 'unknown';
  if (s < 0.7) return 'learning';
  return 'known';
};

/**
 * Get status color class for badges
 * New: red | Learning: yellow | Familiar: green | Known/Mastered: neutral (white/black)
 */
export const getStatusColorClass = (status) => {
  if (status === 'mastered' || status === 'known') return 'bg-foreground/10 text-foreground border-foreground/20';
  if (status === 'familiar') return 'bg-green-500/20 text-green-400 border-green-400/50';
  if (status === 'learning') return 'bg-yellow-500/20 text-yellow-400 border-yellow-400/50';
  return 'bg-red-500/20 text-red-400 border-red-400/50';
};

/**
 * Format strength as percentage
 */
export const formatStrength = (strength) => {
  return `${Math.round((strength || 0) * 100)}%`;
};

/**
 * Get text direction based on language
 */
export const getTextDirection = (language) => {
  if (!language) return 'ltr';
  const code = language.code?.toLowerCase();
  const name = language.name?.toLowerCase();
  const rtlCodes = ['ar', 'fa', 'ps', 'ur', 'he', 'yi', 'per', 'fas', 'ara', 'heb', 'yid', 'prs'];
  const rtlNames = ['arabic', 'persian', 'farsi', 'pashto', 'urdu', 'hebrew', 'yiddish'];
  
  if (rtlCodes.includes(code)) return 'rtl';
  if (rtlNames.some(n => name?.includes(n))) return 'rtl';
  return 'ltr';
};

/**
 * Check if language is RTL
 */
export const isRTL = (language) => {
  return getTextDirection(language) === 'rtl';
};

/**
 * Check if language uses Arabic script (for font selection)
 */
export const isArabicScript = (language) => {
  if (!language) return false;
  const script = language.script?.toLowerCase();
  const code = language.code?.toLowerCase();
  const name = language.name?.toLowerCase();
  if (script === 'arabic') return true;
  if (['ar', 'fa', 'ps', 'ur', 'per', 'fas', 'ara', 'prs'].includes(code)) return true;
  if (['arabic', 'persian', 'farsi', 'pashto', 'urdu'].some(n => name?.includes(n))) return true;
  return false;
};

/**
 * Check if language is Chinese/Japanese/Korean (for character-based display)
 */
export const isChinese = (language) => {
  if (!language) return false;
  const code = language.code?.toLowerCase();
  return ['zh', 'ja', 'ko'].includes(code);
};
